import { GridColumnRequest, ReportGridRequest } from '@/api/services/common.dto'
import i18n from '@/lang/i18n'
import { StatisticalAnalysisGeneralStatisticsDossierDetailViewTableContentCase } from '@/modules/statistical-analysis-module/services/general-statistics-dossier/general-statistics-dossier.dto'
import { generalStatisticsDossierService } from '@/modules/statistical-analysis-module/services/general-statistics-dossier/general-statistics-dossier.service'
import { StatisticalAnalysisTemporalFilterEnum } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-temporal-filter/statistical-analysis-temporal-filter.model'
import { StatisticalAnalysisViewDetailTableFilters } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail-table/statistical-analysis-view-detail-table.model'
import { getSelectedRowsIds, parseGroupingParams } from '@/modules/statistical-analysis-module/shared/helpers/helpers'
import { StatiscalAnalysisTypeCases, StatisticalAnalysisDossiersDatasourceResponsesDetailTypes, StatisticalAnalysisDossiersDatasourceResponsesTypes, StatisticalAnalysisDynamicObject, StatisticalAnalysisViewDetailTableGroupingColumns } from '@/modules/statistical-analysis-module/shared/models/shared.model'
import { StatisticalAnalysisGroupingResponseField } from '@/modules/statistical-analysis-module/shared/services/statistical-analysis-grouping/statistical-analysis-grouping.dto'
import { StatisticalAnalysisGeneralDossierExternalInfo } from '@/modules/statistical-analysis-module/views/general-statistics-dossier/general-statistics-dossier.model'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import { Nullable } from '@/shared/types/app.types'
import { AxiosResponse } from 'axios'
import { chain, extend, reduce } from 'lodash'
import { CommonExportType } from '@/api/resources/common/shared/models/common.models'

export class StatisticalAnalysisHandlerExportTableGeneralDossier {
    protected statisticalAnalysisFilters: Nullable<StatisticalAnalysisViewDetailTableFilters>
    protected selectedRows: StatisticalAnalysisDossiersDatasourceResponsesTypes[] | StatisticalAnalysisDossiersDatasourceResponsesDetailTypes[]
    protected columns: GridColumnRequest[]

    constructor(statisticalAnalysisFilters: Nullable<StatisticalAnalysisViewDetailTableFilters>, selectedRows: StatisticalAnalysisDossiersDatasourceResponsesTypes[] | StatisticalAnalysisDossiersDatasourceResponsesDetailTypes[], columns: GridColumnRequest[]) {
        this.statisticalAnalysisFilters = statisticalAnalysisFilters
        this.selectedRows = selectedRows
        this.columns = columns
    }

    public async exportTo(exportType: CommonExportType): Promise<AxiosResponse> {
        const sidebarFilter = this.statisticalAnalysisFilters?.externalFiltering.sidebarFilters as StatisticalAnalysisGeneralDossierExternalInfo
        const entityIdentifiersRequest: EntityIdentifiersRequest = {
            entityIdentifiers: []
        }

        const request: ReportGridRequest = {
            sectionLabel: `${i18n.tc(sidebarFilter.trackingGeneralSelected?.label as string)} - ${i18n.tc('statisticalAnalysis.dossiers.generalStatistics.detailFor')} ${i18n.tc(sidebarFilter.trackingDetailSelected?.label as string)}`,
            columns: this.columns,
            entityIdentifiersRequest
        }
        const selectedRows = this.selectedRows as StatisticalAnalysisGeneralStatisticsDossierDetailViewTableContentCase[]
        const groupValueFilterRequests: StatisticalAnalysisDynamicObject[] = reduce(selectedRows, (aggr: StatisticalAnalysisDynamicObject[], value: StatisticalAnalysisGeneralStatisticsDossierDetailViewTableContentCase) => {
            const valueWithOmittedColumns = chain(value)
                .omitBy((_value: unknown, key: string) => key.startsWith('stats'))
                .omitBy((value: unknown) => value === null)
                .reduce((aggr: StatisticalAnalysisDynamicObject, value: unknown) => {
                    return parseGroupingParams(aggr, value as StatisticalAnalysisViewDetailTableGroupingColumns)
                }, {})
                .value()
            aggr.push(valueWithOmittedColumns)
            return aggr
        }, [])
        const infoDetail = this.statisticalAnalysisFilters?.temporalFilter as StatisticalAnalysisTemporalFilterEnum
        const groupFields = (this.statisticalAnalysisFilters?.grouping as StatisticalAnalysisGroupingResponseField[]).map((item: StatisticalAnalysisGroupingResponseField) => item.fieldName)
        return generalStatisticsDossierService.detailExportTo(
            this.statisticalAnalysisFilters?.externalFiltering.sidebarFilters as StatisticalAnalysisGeneralDossierExternalInfo,
            request,
            groupValueFilterRequests,
            infoDetail,
            groupFields,
            exportType
        )
    }

    public async exportExternal(row: StatisticalAnalysisDossiersDatasourceResponsesTypes, exportType: CommonExportType): Promise<AxiosResponse> {
        const sidebarFilter = this.statisticalAnalysisFilters?.externalFiltering.sidebarFilters as StatisticalAnalysisGeneralDossierExternalInfo
        const entitySelected = sidebarFilter?.typeSearch?.id as StatiscalAnalysisTypeCases
        const entityIdentifiersRequest: EntityIdentifiersRequest = {
            entityIdentifiers: getSelectedRowsIds(entitySelected, this.selectedRows as StatisticalAnalysisDossiersDatasourceResponsesDetailTypes[])
        }
        const request: ReportGridRequest = {
            sectionLabel: `${i18n.tc(sidebarFilter.trackingGeneralSelected?.label as string)} - ${i18n.tc('statisticalAnalysis.dossiers.generalStatistics.detailFor')} ${i18n.tc(sidebarFilter.trackingDetailSelected?.label as string)}`,
            columns: this.columns,
            entityIdentifiersRequest
        }
        const groupValueFilterRequests: StatisticalAnalysisDynamicObject = chain(row)
            .omitBy((_value: unknown, key: string) => key.startsWith('stats'))
            .omitBy((value: unknown) => value === null)
            .reduce((aggr: StatisticalAnalysisDynamicObject, value: unknown) => {
                return parseGroupingParams(aggr, value as StatisticalAnalysisViewDetailTableGroupingColumns)
            }, {})
            .value()
        const infoDetail = this.statisticalAnalysisFilters?.temporalFilter as StatisticalAnalysisTemporalFilterEnum
        return generalStatisticsDossierService.detailExternalExportTo(
            this.statisticalAnalysisFilters?.externalFiltering.sidebarFilters as StatisticalAnalysisGeneralDossierExternalInfo,
            request,
            groupValueFilterRequests,
            infoDetail,
            exportType
        )
    }
}
